/**
 * G03: UUG Service
 * Address geocoding via GUGiK UUG API
 */

import type { GeocodingResult, GeocodingError, Result, WGS84Coordinate, PL2000Coordinate } from './types'
import { ok, err } from './types'
import { coordinateService } from './CoordinateService'

const UUG_BASE_URL = 'https://services.gugik.gov.pl/uug/'
const MAX_RETRIES = 3
const RETRY_DELAYS = [1000, 2000, 4000]  // Exponential backoff
const TIMEOUT_MS = 10000

export class UUGService {
  private static instance: UUGService

  static getInstance(): UUGService {
    if (!UUGService.instance) {
      UUGService.instance = new UUGService()
    }
    return UUGService.instance
  }

  /**
   * Geocode a Polish address to coordinates
   * @param address - Full address string, e.g., "Warszawa, Marszałkowska 1"
   */
  async geocode(address: string): Promise<Result<GeocodingResult, GeocodingError>> {
    const trimmed = address.trim()
    if (!trimmed) {
      return err({
        type: 'not_found',
        message: 'Address cannot be empty'
      })
    }

    const url = `${UUG_BASE_URL}?request=GetAddress&address=${encodeURIComponent(trimmed)}`

    let lastError: Error | null = null

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS)

        const response = await fetch(url, {
          signal: controller.signal,
          headers: {
            'Accept': 'application/json'
          }
        })

        clearTimeout(timeoutId)

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const data = await response.json()
        return this.parseResponse(data, trimmed)

      } catch (error) {
        lastError = error as Error

        if ((error as Error).name === 'AbortError') {
          lastError = new Error('Request timed out')
        }

        // Wait before retry (except on last attempt)
        if (attempt < MAX_RETRIES - 1) {
          await this.delay(RETRY_DELAYS[attempt])
        }
      }
    }

    return err({
      type: 'network',
      message: lastError?.message || 'Network error after retries'
    })
  }

  private parseResponse(data: unknown, originalAddress: string): Result<GeocodingResult, GeocodingError> {
    try {
      // UUG returns { results: { "1": {...}, "2": {...} } } or { results: null }
      if (!data || typeof data !== 'object') {
        return err({
          type: 'parse',
          message: 'Invalid response format'
        })
      }

      const response = data as Record<string, unknown>

      // Check for error
      if (response.error) {
        return err({
          type: 'not_found',
          message: String(response.error)
        })
      }

      // Get results object (API returns object with numeric keys, not array)
      const resultsObj = response.results as Record<string, Record<string, unknown>> | null

      if (!resultsObj || typeof resultsObj !== 'object') {
        return err({
          type: 'not_found',
          message: `No results found for: ${originalAddress}`
        })
      }

      // Convert object to array
      const results = Object.values(resultsObj)

      if (results.length === 0) {
        return err({
          type: 'not_found',
          message: `No results found for: ${originalAddress}`
        })
      }

      // If multiple results, check confidence
      if (results.length > 1) {
        const topResult = results[0]
        const accuracy = Number(topResult.accuracy || topResult.score || 1)

        if (accuracy < 0.7) {
          return err({
            type: 'ambiguous',
            message: 'Multiple possible matches found',
            suggestions: results.slice(0, 5).map(r => {
              // Build address from available fields
              const city = r.city || ''
              const street = r.street || ''
              const number = r.number || ''
              return `${street} ${number}, ${city}`.trim() || String(r.address || r.name || '')
            })
          })
        }
      }

      // Use first result
      const result = results[0]

      // Extract coordinates (PL-2000)
      const x = Number(result.x)
      const y = Number(result.y)

      if (isNaN(x) || isNaN(y)) {
        return err({
          type: 'parse',
          message: 'Invalid coordinates in response'
        })
      }

      const pl2000: PL2000Coordinate = { x, y }
      const wgs84: WGS84Coordinate = coordinateService.pl2000ToWgs84(pl2000)

      // Build address from available fields
      const city = String(result.city || '')
      const street = String(result.street || '')
      const number = String(result.number || '')
      const addressStr = street && number
        ? `${street} ${number}, ${city}`
        : city || originalAddress

      const geocodingResult: GeocodingResult = {
        address: addressStr,
        pl2000,
        wgs84,
        confidence: Number(result.accuracy || result.score || 1),
        raw: result
      }

      return ok(geocodingResult)

    } catch (error) {
      return err({
        type: 'parse',
        message: `Failed to parse response: ${(error as Error).message}`
      })
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
}

// Export singleton instance
export const uugService = UUGService.getInstance()
